import React, { useEffect, useRef } from 'react';
import {
  DialogActions,
  DialogContainer,
  DialogContent,
  DialogHeader,
} from './styles';
import {
  Button,
  DialogProps as MuiDialogProps,
  DialogTitle,
} from '@mui/material';
import classNames from 'classnames';
import CloseIcon from '@/public/icons/close_black.svg';
import { ButtonWrapper } from '@/components/@IntranetLibrary/Button/ButtonWrapper';

export type DialogType = 'confirm' | 'alert';

export type DialogOptions = {
  id?: string;
  type?: DialogType;
  title?: React.ReactNode;
  message?: React.ReactNode;
  invisibleButton?: boolean;
  showCloseButton?: boolean;
  cancelButtonText?: string;
  confirmButtonText?: string;
  onCancel?: () => Promise<void> | void;
  onConfirm?: () => Promise<void> | void;
  onAfterClose?: () => void;
  onClickBackdrop?: () => void;
  isBlockBackDropClose?: boolean;
  /** ESC 로 닫히지 않게 — 버튼으로만 결정해야 하는 안내 (PORTAL-12588) */
  isBlockEscapeClose?: boolean;
  forceToFront?: boolean; // 최상위 z-index
  dialogProps?: MuiDialogProps;
};

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  onClickCancel: () => void;
  onClickConfirm: () => void;
  dialogOptions: DialogOptions;
};

const Dialog = ({
  open,
  onClose,
  onClickCancel,
  onClickConfirm,
  dialogOptions,
}: DialogProps) => {
  const {
    type,
    title,
    message,
    cancelButtonText,
    confirmButtonText,
    dialogProps,
    forceToFront,
    isBlockBackDropClose,
    isBlockEscapeClose,
    onClickBackdrop,
    invisibleButton = false,
    showCloseButton = true,
  } = dialogOptions;

  // 중복 실행 방지를 위한 ref
  const isProcessingRef = useRef(false);

  // Dialog가 열릴 때마다 ref 리셋
  useEffect(() => {
    if (open) {
      isProcessingRef.current = false;
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleCancel = () => {
    // 이미 처리 중이면 무시
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    onClickCancel();
    handleClose();
  };

  const handleConfirm = () => {
    // 이미 처리 중이면 무시
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    onClickConfirm();
    handleClose();
  };

  return (
    <DialogContainer
      {...dialogProps}
      onKeyDown={(e) => {
        if (e.nativeEvent.isComposing) return;
        if (e.key !== 'Enter') return;
        if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
        // 엔터 키를 계속 누르고 있는 경우(repeat) 무시
        if (e.repeat) return;

        const target = e.target as HTMLElement;
        const tag = target.tagName.toLowerCase();
        const isTextArea = tag === 'textarea';
        const isEditable = target.isContentEditable;

        // (추가) 버튼이나 role="button" 요소에 포커스가 있을 땐 기본 동작에 맡기기
        const isButtonish =
          tag === 'button' ||
          target.getAttribute('role') === 'button' ||
          !!target.closest('button,[role="button"]');

        if (isTextArea || isEditable || isButtonish) return;

        e.preventDefault();
        handleConfirm();
      }}
      open={open}
      className={classNames({ 'high-z-index': forceToFront })}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          onClickBackdrop?.();
          if (isBlockBackDropClose) return;
        }
        if (reason === 'escapeKeyDown' && isBlockEscapeClose) return;
        handleClose();
      }}
    >
      <DialogHeader
        className={classNames('dialog-header', { hideTitle: !title })}
      >
        <DialogTitle>{title}</DialogTitle>
        {showCloseButton && (
          <Button className={'close'} onClick={handleClose}>
            <CloseIcon width={'24px'} height={'24px'} />
          </Button>
        )}
      </DialogHeader>

      <DialogContent className={classNames('dialog-content')}>
        {message}
      </DialogContent>

      {!invisibleButton && (
        <DialogActions>
          <ButtonWrapper>
            {type === 'confirm' && (
              <Button
                variant={'outlined'}
                size={'large'}
                onClick={handleCancel}
              >
                {cancelButtonText}
              </Button>
            )}
            <Button
              variant={'contained'}
              size={'large'}
              onClick={handleConfirm}
            >
              {confirmButtonText}
            </Button>
          </ButtonWrapper>
        </DialogActions>
      )}
    </DialogContainer>
  );
};

export default Dialog;
